import React, { useEffect, useState } from 'react';

interface SessionWarningModalProps {
  isOpen: boolean;
  onExtend: () => void;
  onLogout: () => void;
}

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  isOpen,
  onExtend,
  onLogout,
}) => {
  const [countdown, setCountdown] = useState(120); // 2 minutes in seconds

  useEffect(() => {
    if (!isOpen) {
      setCountdown(120);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onLogout]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[72px] right-0 bottom-0 z-[9999] flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-[#621C43] max-w-md w-full rounded-xl text-white relative shadow-xl border border-[#7a2757]">
        <div className="border-b p-4 border-[#FFF8ED]">
          <h2 className="text-lg font-semibold text-center">Session Expiring Soon</h2>
        </div>

        <div className="text-center p-6">
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-pulse">
            <svg
              className="w-10 h-10 text-white drop-shadow-sm"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-[#FFF8ED] font-medium leading-relaxed mb-4">
            Your session will expire in:
          </p>
          <div className="text-3xl font-bold text-yellow-300 mb-4">{formatTime(countdown)}</div>
          <p className="text-[#EEE] text-sm">
            Click "Stay Logged In" to extend your session or you will be automatically logged out.
          </p>
        </div>

        <div className="flex justify-between gap-3 py-5 px-6 border-t border-black">
          <button
            onClick={onLogout}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-md text-sm"
          >
            Logout Now
          </button>
          <button
            onClick={onExtend}
            className="flex-1 bg-[#4C86E7] hover:bg-[#4679d1] rounded-md py-2 text-sm"
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionWarningModal;
